import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Edit, Plus, Trash, HelpCircle } from "lucide-react";
import { StreamDialog } from "./StreamDialog";
import { DeleteStreamDialog } from "./DeleteStreamDialog";
import { useToast } from "@/components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface StreamListProps {
  classId: string;
}

export function StreamList({ classId }: StreamListProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isStreamDialogOpen, setIsStreamDialogOpen] = useState(false);
  const [selectedStream, setSelectedStream] = useState<string | null>(null);
  const [streamToDelete, setStreamToDelete] = useState<string | null>(null);

  const { data: streams, isLoading } = useQuery({
    queryKey: ['class_streams', classId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('class_streams')
        .select('*')
        .eq('class_id', classId)
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  const updateStreamMutation = useMutation({
    mutationFn: async ({ streamId, preserve }: { streamId: string; preserve: boolean }) => {
      const { error } = await supabase
        .from('class_streams')
        .update({ preserve_on_promotion: preserve })
        .eq('id', streamId);
      
      if (error) throw error;
    },
    onMutate: async ({ streamId, preserve }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['class_streams', classId] });

      // Snapshot the previous value
      const previousStreams = queryClient.getQueryData(['class_streams', classId]);

      // Optimistically update to the new value
      queryClient.setQueryData(['class_streams', classId], (old: any) => {
        return old?.map((stream: any) =>
          stream.id === streamId
            ? { ...stream, preserve_on_promotion: preserve }
            : stream
        );
      });

      return { previousStreams };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(['class_streams', classId], context?.previousStreams);
      toast({
        title: "Error updating stream preservation",
        description: "Failed to update stream settings. Please try again.",
        variant: "destructive",
      });
    },
    onSuccess: (data, { preserve }) => {
      toast({
        title: "Stream updated",
        description: `Stream will ${preserve ? 'be preserved' : 'not be preserved'} during promotion`,
      });
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we're up to date
      queryClient.invalidateQueries({ queryKey: ['class_streams', classId] });
    },
  });

  const handleAddStream = () => {
    setSelectedStream(null);
    setIsStreamDialogOpen(true);
  };

  const handleEditStream = (streamId: string) => {
    setSelectedStream(streamId);
    setIsStreamDialogOpen(true);
  };

  if (isLoading) {
    return <div>Loading streams...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-4 w-4">
                <HelpCircle className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[300px]">
              <p>Streams can be assigned to students using the Bulk Edit feature in the Student Management page. 
                 When "Preserve on Promotion" is enabled, students will keep their stream assignment when promoted 
                 if the same stream exists in the next class.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button onClick={handleAddStream}>
          <Plus className="h-4 w-4 mr-2" />
          Add Stream
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Stream Name</TableHead>
            <TableHead>
              <div className="flex items-center gap-2">
                Preserve on Promotion
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-4 w-4">
                        <HelpCircle className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>If enabled, students will keep their stream when promoted to the next class, 
                         but only if the same stream exists in that class.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {streams?.map((stream) => (
            <TableRow key={stream.id}>
              <TableCell>{stream.name}</TableCell>
              <TableCell>
                <Switch
                  checked={stream.preserve_on_promotion}
                  onCheckedChange={(checked) => {
                    updateStreamMutation.mutate({
                      streamId: stream.id,
                      preserve: checked
                    });
                  }}
                />
              </TableCell>
              <TableCell>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleEditStream(stream.id)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => setStreamToDelete(stream.id)}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <StreamDialog
        open={isStreamDialogOpen}
        onOpenChange={setIsStreamDialogOpen}
        classId={classId}
        selectedStream={selectedStream}
      />

      <DeleteStreamDialog
        streamId={streamToDelete}
        onClose={() => setStreamToDelete(null)}
      />
    </div>
  );
}